import React, { useState } from 'react';
import { X, Rocket } from 'lucide-react';

const AddTagModal = ({
  show,
  onClose,
  product,
  onSave,
  existingBranches = []
}) => {
  const [formData, setFormData] = useState({
    tag: '',
    branch_name: 'main',
    status: 'Success',
    environment: 'DEV',
    deployment_type: '',
    commit_id: '',
    commit_message: '',
    author_name: '',
    domain_name: '',
    image_name: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!show || !product) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.tag.trim()) {
      setError('Tag / Version is required');
      return;
    }
    setError('');
    setIsSubmitting(true);
    try {
      await onSave({
        ...formData,
        product_id: product.id,
        tag: formData.tag.trim(),
        branch_name: formData.branch_name.trim() || 'main',
        deployment_type: formData.deployment_type || null
      });
      // Reset form
      setFormData({
        tag: '',
        branch_name: 'main',
        status: 'Success',
        environment: 'DEV',
        deployment_type: '',
        commit_id: '',
        commit_message: '',
        author_name: '',
        domain_name: '',
        image_name: ''
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to add tag');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-gray-900 rounded-xl border border-gray-800 w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-800 flex items-center justify-between bg-gradient-to-r from-gray-900 to-gray-800/50">
          <div className="flex items-center gap-2.5">
            <Rocket className="w-5 h-5 text-blue-400" />
            <div>
              <h2 className="text-lg font-bold text-white">Add Tag / Deployment</h2>
              <p className="text-xs text-gray-400">{product.name} ({product.id})</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-3.5">
          {error && (
            <div className="p-2.5 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            {/* Tag / Build Number */}
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">
                Tag / Build Number <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.tag}
                onChange={(e) => setFormData({ ...formData, tag: e.target.value })}
                placeholder="e.g. 26 or v2.1.0"
                className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-xs"
              />
            </div>

            {/* Branch Name */}
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">
                Branch <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                required
                list="branch-options"
                value={formData.branch_name}
                onChange={(e) => setFormData({ ...formData, branch_name: e.target.value })}
                placeholder="main"
                className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-xs"
              />
              <datalist id="branch-options">
                {existingBranches.map((b) => (
                  <option key={b} value={b} />
                ))}
              </datalist>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {/* Status */}
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-xs"
              >
                <option value="Success">Success</option>
                <option value="Failed">Failed</option>
                <option value="Pending">Pending</option>
              </select>
            </div>

            {/* Environment */}
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Environment</label>
              <select
                value={formData.environment}
                onChange={(e) => setFormData({ ...formData, environment: e.target.value })}
                className="w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-xs"
              >
                <option value="DEV">DEV</option>
                <option value="Production">Production</option>
                <option value="Staging">Staging</option>
                <option value="QA">QA</option>
              </select>
            </div>

            {/* Deployment Type */}
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Type</label>
              <select
                value={formData.deployment_type}
                onChange={(e) => setFormData({ ...formData, deployment_type: e.target.value })}
                className="w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-xs"
              >
                <option value="">None</option>
                <option value="live">LIVE</option>
                <option value="previous">PREVIOUS</option>
              </select>
            </div>
          </div>

          {/* Commit ID & Author */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Commit ID</label>
              <input
                type="text"
                value={formData.commit_id}
                onChange={(e) => setFormData({ ...formData, commit_id: e.target.value })}
                placeholder="git commit sha"
                className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-xs font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Author Name</label>
              <input
                type="text"
                value={formData.author_name}
                onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
                placeholder="e.g. devops / john"
                className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-xs"
              />
            </div>
          </div>

          {/* Commit Message */}
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-1">Commit Message</label>
            <input
              type="text"
              value={formData.commit_message}
              onChange={(e) => setFormData({ ...formData, commit_message: e.target.value })}
              placeholder="e.g. Manual deployment after pipeline fix"
              className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-xs"
            />
          </div>

          {/* Domain & Image Name */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Domain Name</label>
              <input
                type="text"
                value={formData.domain_name}
                onChange={(e) => setFormData({ ...formData, domain_name: e.target.value })}
                placeholder="api.example.com"
                className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-xs font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Image Name</label>
              <input
                type="text"
                value={formData.image_name}
                onChange={(e) => setFormData({ ...formData, image_name: e.target.value })}
                placeholder="repository/image:tag"
                className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-xs font-mono"
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="p-3.5 border-t border-gray-800 bg-gray-900/80 flex justify-end gap-2.5">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-1.5 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors text-xs font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-xs font-medium flex items-center gap-1.5 shadow-md shadow-blue-600/20 disabled:opacity-50"
          >
            {isSubmitting ? 'Adding...' : 'Save Tag'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddTagModal;
